import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import { RoleLiteDto } from '../../../api/role/models/RoleDto'
import { useSafeUserData } from '../../../stateStores/User'
import roleApi from '../../../api/role/RoleApi'
import LayoutSingleView from '../../common/layout/LayoutSingleView'
import { MenuItems } from '../../../enums/MenuItems'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import Section from '../../common/sections/Section'
import { AuthHelper } from '../../../auth/AuthHelper'
import Button from '../../common/form/inputs/Button'
import { UserRoles } from '../../../api/tenant/models/UserDto'
import { Pages } from '../../../Pages'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import RoleCard from '../RoleCard'
import Cards from '../../common/cards/Cards'
import NoRecords from '../../common/pagePlaceholders/NoRecords'

export default function RoleList(): React.JSX.Element {
    const [roles, setRoles] = useState<RoleLiteDto[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const { user, tenant } = useSafeUserData()
    const canCreateRoleAsTA = AuthHelper.userHasRoleForTenant(
        user,
        UserRoles.TA,
        tenant.id,
    )
    const canCreateRole =
        canCreateRoleAsTA ||
        AuthHelper.userHasRoleForTenant(user, UserRoles.HM, tenant.id)

    useEffect(() => {
        let cancelled = false

        const fetchData = async () => {
            setIsLoading(true)
            setErrMsg(undefined)

            try {
                const newRoles = (
                    await roleApi.getAllLite({
                        includeHiringTeamIds: true,
                        includeCompanyLinkIds: false,
                    })
                ).roles

                if (!cancelled) {
                    setRoles(newRoles)
                }
            } catch (error) {
                console.error(error)
                const msg =
                    'There was a problem loading roles. Please refresh the page to try again. Error details: '
                const errorMessage =
                    error instanceof Error
                        ? `${msg}${error.message}`
                        : `${msg}An unknown error occurred.`

                if (!cancelled) {
                    setErrMsg(errorMessage)
                    setRoles([])
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        fetchData()

        return () => {
            cancelled = true
        }
    }, [tenant.id])

    if (isLoading) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <LoadingSpinner />
            </LayoutSingleView>
        )
    }

    if (errMsg) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Note type={NoteType.ERROR}>{errMsg}</Note>
            </LayoutSingleView>
        )
    }

    if (!isLoading && !errMsg && roles.length === 0 && canCreateRoleAsTA) {
        return (
            <Navigate
                to={Pages.CreateNewRole}
                replace
                state={{ hideCancel: true }}
            />
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Roles}>
            <Section
                title="Roles"
                titleType="h2"
                buttons={
                    roles.length > 0 &&
                    canCreateRole && (
                        <>
                            <Button
                                style="primary"
                                to={Pages.CreateNewRoleLLM}
                                type="link"
                                size="small"
                            >
                                <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className={`mr-2`}
                                />
                                New Role
                            </Button>
                            {/* {canCreateRoleAsTA && (
                                <Button
                                    style="secondary"
                                    to={Pages.CreateNewRole}
                                    type="link"
                                    size="small"
                                >
                                    <FontAwesomeIcon
                                        icon={faPlusCircle}
                                        className={`mr-2`}
                                    />
                                    New Role as TA
                                </Button>
                            )} */}
                        </>
                    )
                }
            >
                {roles.length === 0 &&
                    (canCreateRole ? (
                        <NoRecords
                            imgSrc="job_role.svg"
                            caption="Add a job role to start finding candidates"
                        >
                            <Button
                                style="primary"
                                type="link"
                                classes="mt-4"
                                to={Pages.CreateNewRole}
                            >
                                <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className="mr-2"
                                />
                                New Role
                            </Button>
                            {/* {canCreateRoleAsTA && (
                                <Button
                                    style="secondary"
                                    type="link"
                                    classes="mt-4"
                                    to={Pages.CreateNewRole}
                                >
                                    <FontAwesomeIcon
                                        icon={faPlusCircle}
                                        className="mr-2"
                                    />
                                    New Role as TA
                                </Button>
                            )} */}
                        </NoRecords>
                    ) : (
                        <NoRecords
                            imgSrc="job_role.svg"
                            caption="No job roles available - has the TA added you to the hiring team for a job role?"
                        >
                            <></>
                        </NoRecords>
                    ))}
                {roles.length > 0 && (
                    <Cards className="mt-4">
                        {roles.map((role) => (
                            <RoleCard
                                role={role}
                                key={`role-card-${role.id}`}
                            />
                        ))}
                    </Cards>
                )}
            </Section>
        </LayoutSingleView>
    )
}
